import React, { useEffect, useState } from "react";
import { ReactComponent as FilterIcon } from "../../../Assets/ActionIcons/FilterIcon.svg";
import { InvoiceStatus } from "../../../Contracts/Invoice";
import CloseIcon from "../../../Assets/ActionIcons/CloseIcon.svg";

type AllEnabledDisabled = "all" | "enabled" | "disabled";

interface InvoiceFilters {
  invoiceStatus: InvoiceStatus | "all";
  autoCollect: AllEnabledDisabled;
  dateIssuedStart: Date | null;
  dateIssuedEnd: Date | null;
  dueDateStart: Date | null;
  dueDateEnd: Date | null;
  scheduleDate: Date | null;
}

const defaultFilters: InvoiceFilters = {
  invoiceStatus: "all",
  autoCollect: "all",
  dateIssuedStart: null,
  dateIssuedEnd: null,
  dueDateStart: null,
  dueDateEnd: null,
  scheduleDate: null
};

export interface ClientPortalInvoiceListPageFiltersProps {
  setFilter: (filter: string) => void;
  defaultFilter?: string;
}

const ClientPortalInvoiceListPageFilters: React.FC<ClientPortalInvoiceListPageFiltersProps> = ({
  setFilter,
  defaultFilter = ""
}) => {
  const [filterMenuOpen, setFilterMenuOpen] = React.useState(false);
  const filterMenuRef = React.useRef<HTMLDivElement>(null);

  // consolidated input states
  const [inputs, setInputs] = useState<InvoiceFilters>(defaultFilters);

  // Dirty fields
  const [dirty, setDirty] = useState<Partial<InvoiceFilters>>({});

  const handleChange = <T extends keyof InvoiceFilters>(key: T, value: InvoiceFilters[T]) => {
    setDirty({ ...dirty, [key]: value });
  };

  const handleFilterRemove = (field: keyof InvoiceFilters) => {
    setInputs({ ...inputs, [field]: defaultFilters[field] });
    const filtersToApply = generateFilterConditions(true)
      .filter(entry => entry.fieldName !== field)
      .map(({ applyFilter }) => applyFilter);
    setFilter([defaultFilter, ...filtersToApply].join(";"));
  };
  const generateFilterConditions = (
    includeDirty: boolean
  ): Array<{
    condition: boolean;
    fieldName: keyof InvoiceFilters;
    applyFilter: string;
    appliedLabel: string;
  }> => {
    let consolidatedDataForInputs = inputs;
    if (includeDirty) {
      consolidatedDataForInputs = {
        ...inputs,
        ...dirty
      };
    }

    return [
      {
        condition: consolidatedDataForInputs.invoiceStatus !== "all",
        fieldName: "invoiceStatus" as keyof InvoiceFilters,
        applyFilter: `status:eq:${consolidatedDataForInputs.invoiceStatus}`,
        appliedLabel: `Status: ${consolidatedDataForInputs.invoiceStatus}`
      },
      {
        condition: consolidatedDataForInputs.autoCollect !== "all",
        fieldName: "autoCollect" as keyof InvoiceFilters,
        applyFilter: `payments_invoice_auto_collection_status:${
          consolidatedDataForInputs.autoCollect === "enabled" ? "eq" : "ne"
        }:pending`,
        appliedLabel: `Auto Collect: ${
          consolidatedDataForInputs.autoCollect.charAt(0).toUpperCase() +
          consolidatedDataForInputs.autoCollect.slice(1)
        }`
      },
      {
        condition: consolidatedDataForInputs.dateIssuedStart !== null,
        fieldName: "dateIssuedStart" as keyof InvoiceFilters,
        applyFilter: `issue_date:gte:${consolidatedDataForInputs.dateIssuedStart}`,
        appliedLabel: `Date Issued: ${consolidatedDataForInputs.dateIssuedStart}`
      },
      {
        condition: consolidatedDataForInputs.dateIssuedEnd !== null,
        fieldName: "dateIssuedEnd" as keyof InvoiceFilters,
        applyFilter: `issue_date:lte:${consolidatedDataForInputs.dateIssuedEnd}`,
        appliedLabel: `Date Issued: ${consolidatedDataForInputs.dateIssuedEnd}`
      },
      {
        condition: consolidatedDataForInputs.dueDateStart !== null,
        fieldName: "dueDateStart" as keyof InvoiceFilters,
        applyFilter: `due_date:gte:${consolidatedDataForInputs.dueDateStart}`,
        appliedLabel: `Due Date: ${consolidatedDataForInputs.dueDateStart}`
      },
      {
        condition: consolidatedDataForInputs.dueDateEnd !== null,
        fieldName: "dueDateEnd" as keyof InvoiceFilters,
        applyFilter: `due_date:lte:${consolidatedDataForInputs.dueDateEnd}`,
        appliedLabel: `Due Date: ${consolidatedDataForInputs.dueDateEnd}`
      },
      {
        condition: consolidatedDataForInputs.scheduleDate !== null,
        fieldName: "scheduleDate" as keyof InvoiceFilters,
        applyFilter: `schedule_date:eq:${consolidatedDataForInputs.scheduleDate}`,
        appliedLabel: `Schedule Date: ${consolidatedDataForInputs.scheduleDate}`
      }
    ].filter(entry => entry.condition);
  };

  const onApply = () => {
    setInputs(inputs => ({ ...inputs, ...dirty }));
    setDirty({});
    const filtersToApply = generateFilterConditions(true).map(({ applyFilter }) => applyFilter);
    setFilter([defaultFilter, ...filtersToApply].join(";"));
    setFilterMenuOpen(false);
  };

  const appliedFilters = generateFilterConditions(false);

  const onReset = () => {
    setInputs(defaultFilters);
    setDirty({});
    setFilter(defaultFilter);
    setFilterMenuOpen(false);
  };

  const consolidatedDataForInputs = {
    ...inputs,
    ...dirty
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
        setFilterMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [filterMenuRef]);

  return (
    <div className="relative flex items-center">
      <div
        className="border rounded p-2 ml-2 cursor-pointer hover:bg-gray-100 hover:scale-110"
        onClick={() => setFilterMenuOpen(!filterMenuOpen)}
      >
        <FilterIcon />
      </div>
      {appliedFilters.length > 0 && (
        <div className="flex space-x-2 items-center">
          {appliedFilters.map(appliedFilter => (
            <div
              key={appliedFilter.fieldName}
              className="bg-gray-100 px-2 py-1 rounded-lg flex items-center space-x-1 mx-2"
            >
              <span>{appliedFilter.appliedLabel}</span>
              <button
                className="rounded-full hover:bg-gray-200 flex items-center justify-center p-1"
                onClick={() => handleFilterRemove(appliedFilter.fieldName)}
              >
                <img src={CloseIcon} alt="close" className="h-2 w-2" />
              </button>
            </div>
          ))}
        </div>
      )}
      {filterMenuOpen && (
        <div
          className="absolute bg-white shadow-lg p-4 top-full left-0 z-40 m-2 w-64 rounded"
          ref={filterMenuRef}
        >
          <div className="flex flex-col space-y-2 mb-4">
            <label>Invoice Status</label>
            <select
              value={consolidatedDataForInputs.invoiceStatus}
              onChange={e => handleChange("invoiceStatus", e.target.value as InvoiceStatus)}
              className="border rounded px-2 py-1"
            >
              <option value="all">All</option>
              <option value={InvoiceStatus.Open}>Open</option>
              <option value={InvoiceStatus.Paid}>Paid</option>
              <option value={InvoiceStatus.Overdue}>Overdue</option>
              <option value={InvoiceStatus.Voided}>Voided</option>
            </select>
          </div>
          <div className="flex flex-col space-y-2 mb-4">
            <label>Auto Collect</label>
            <select
              value={consolidatedDataForInputs.autoCollect}
              onChange={e => handleChange("autoCollect", e.target.value as AllEnabledDisabled)}
              className="border rounded px-2 py-1"
            >
              <option value="all">All</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
          <div className="flex flex-col space-y-2 mb-4">
            <label>Date Issued</label>
            <div className="grid grid-cols-3 gap-2">
              <label>Start</label>
              <input
                type="date"
                className="border rounded px-2 py-1 col-span-2"
                value={consolidatedDataForInputs.dateIssuedStart?.toISOString().split("T")[0]}
                onChange={e => handleChange("dateIssuedStart", new Date(e.target.value))}
              />
              <label>End</label>
              <input
                type="date"
                className="border rounded px-2 py-1 col-span-2"
                value={consolidatedDataForInputs.dateIssuedEnd?.toISOString().split("T")[0]}
                onChange={e => handleChange("dateIssuedEnd", new Date(e.target.value))}
              />
            </div>
          </div>
          <div className="flex flex-col space-y-2 mb-4">
            <label>Due Date</label>
            <div className="grid grid-cols-3 gap-2">
              <label>Start</label>
              <input
                type="date"
                className="border rounded px-2 py-1 col-span-2"
                value={consolidatedDataForInputs.dueDateStart?.toISOString().split("T")[0]}
                onChange={e => handleChange("dueDateStart", new Date(e.target.value))}
              />
              <label>End</label>
              <input
                type="date"
                className="border rounded px-2 py-1 col-span-2"
                value={consolidatedDataForInputs.dueDateEnd?.toISOString().split("T")[0]}
                onChange={e => handleChange("dueDateEnd", new Date(e.target.value))}
              />
            </div>
          </div>
          <div className="flex flex-col space-y-2 mb-4">
            <label>Schedule Date</label>
            <input
              type="date"
              className="border rounded px-2 py-1"
              value={consolidatedDataForInputs.scheduleDate?.toISOString().split("T")[0]}
              onChange={e => handleChange("scheduleDate", new Date(e.target.value))}
            />
          </div>
          <div className="grid grid-cols-2  gap-2">
            <button
              className="bg-blue-500 text-white rounded p-2 hover:bg-blue-700"
              onClick={onApply}
            >
              Apply
            </button>
            <button
              className="bg-gray-100 text-gray-800 rounded p-2 hover:bg-gray-200"
              onClick={onReset}
            >
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortalInvoiceListPageFilters;
